import { PARKING_ACTION_TYPES } from "../_actionTypes/PARKING_ACTION_TYPES";
import { BASE_PAGE_SIZE } from "../_modelsTS/Pagination";
import { ParkingFilter, ParkingSpotType } from "../_models/ParkingSpot";

export interface ParkingState {
    items: ParkingSpotType[];
    offset: number;
    page_size: number;
    total_count: number;
    floors: string[];
    selectedUid: string | null;
    filter: ParkingFilter;
    isRequesting: boolean;
    invitationGroupUid: string | null;
    // Whether the parking config has finished loading at least once.
    // Distinguishes "not configured" (null after load) from "still loading"
    // (null before load) so we don't flash the onboarding banner on mount.
    configLoaded: boolean;
}

const initialState: ParkingState = {
    items: [],
    offset: 0,
    page_size: BASE_PAGE_SIZE,
    total_count: 0,
    floors: [],
    selectedUid: null,
    filter: { status: 'ALL', floor: null, searchTerm: '' },
    isRequesting: false,
    invitationGroupUid: null,
    configLoaded: false,
};

let getRootState = (state: any): ParkingState => state as ParkingState;

export class ParkingSelector {
    static getSpots = (state: any) => getRootState(state).items;
    static getSelectedUid = (state: any) => getRootState(state).selectedUid;
    static getSelectedSpot = (state: any) => {
        const uid = getRootState(state).selectedUid;
        if (!uid) return null;
        return getRootState(state).items.find((s) => s.uid === uid) ?? null;
    };
    static getFilter = (state: any) => getRootState(state).filter;
    static getIsRequesting = (state: any) => getRootState(state).isRequesting;
    static getTotalCount = (state: any) => getRootState(state).total_count;
    static getOffset = (state: any) => getRootState(state).offset;
    static getPageSize = (state: any) => getRootState(state).page_size;
    static getFloors = (state: any) => getRootState(state).floors;
    static getInvitationGroupUid = (state: any) =>
        getRootState(state).invitationGroupUid;
    static getConfigLoaded = (state: any) =>
        getRootState(state).configLoaded;

    static setRootStateFn = (rootStateFn: (s: any) => ParkingState = (s) => s) => {
        getRootState = rootStateFn;
    };
}

export function parking(state: ParkingState = initialState, action: any): ParkingState {
    switch (action.type) {
        case PARKING_ACTION_TYPES.PARKING_LIST_REQUEST:
            return { ...state, isRequesting: true };

        case PARKING_ACTION_TYPES.PARKING_LIST_SUCCESS:
            return {
                ...state,
                items: action.items,
                offset: action.offset ?? state.offset,
                page_size: action.page_size ?? state.page_size,
                total_count: action.total_count ?? action.items.length,
                floors: action.floors ?? state.floors,
                isRequesting: false,
            };

        case PARKING_ACTION_TYPES.PARKING_LIST_FAILURE:
            return { ...state, isRequesting: false };

        case PARKING_ACTION_TYPES.PARKING_SELECT:
            return { ...state, selectedUid: action.spotUid };

        case PARKING_ACTION_TYPES.PARKING_UPDATE_SELECTED_LOCALLY: {
            if (!state.selectedUid) return state;
            return {
                ...state,
                items: state.items.map((s) =>
                    s.uid === state.selectedUid
                        ? { ...s, ...action.patch, edited: true }
                        : s,
                ),
            };
        }

        case PARKING_ACTION_TYPES.PARKING_UPDATE_SUCCESS: {
            const updated: ParkingSpotType = action.spot;
            return {
                ...state,
                items: state.items.map((s) => (s.uid === updated.uid ? updated : s)),
            };
        }

        case PARKING_ACTION_TYPES.PARKING_DELETE_SUCCESS: {
            const deletedUid: string = action.uid;
            return {
                ...state,
                items: state.items.filter((s) => s.uid !== deletedUid),
                total_count: Math.max(0, state.total_count - 1),
                selectedUid:
                    state.selectedUid === deletedUid ? null : state.selectedUid,
            };
        }

        case PARKING_ACTION_TYPES.PARKING_CHANGE_FILTER:
            return {
                ...state,
                filter: { ...state.filter, ...action.filter },
            };

        case PARKING_ACTION_TYPES.PARKING_CHANGE_OFFSET:
            return { ...state, offset: action.offset };

        case PARKING_ACTION_TYPES.PARKING_RESET_PAGINATION:
            return { ...state, offset: 0 };

        case PARKING_ACTION_TYPES.PARKING_CONFIG_SUCCESS:
            return {
                ...state,
                invitationGroupUid: action.invitationGroupUid,
                configLoaded: true,
            };

        default:
            return state;
    }
}
